import * as ko from 'knockout';

import { DimensionMetaData, CV_STAGE_SLUG, TRIAL_TYPE_SLUG } from '../api/dimension_metas';
import { AttributeMeta, makeDynamicAttribute } from './attribute_meta';
import { OrderedEntities } from './helpers/ordered_entities';
import { slugValidation, SlugGenerator } from '../ko_bindings/slug_validation';
import { I18nText, translate } from '../i18n_text';
import { IslandGridAttributeColumn } from 'react-islands';
import { FilterDelegate, convertToParameter, updateFilterValue } from '../components/list_filters';
import { canViewDimensionRecord } from '../permissions';

/**
 * A map of filter attribute name to filter value.
 *
 * @example { attr_1: "apple", attr_2: "oran" }
 */
export type AttributeFilters = Record<string, string | string[]>;

export class DimensionMeta {
  private slugGenerator: SlugGenerator;
  private attributeFilterToSlug: Map<FilterDelegate, string>;

  id = ko.observable<string>(null);
  nameJson = ko.observable<I18nText>().extend({
    i18nTextRequired: true,
    serverError: true,
  });
  isDate = ko.observable<boolean>(false);
  slug = ko.observable<string>(null);
  nameSlug = ko.observable('').extend(slugValidation);
  nameTemplate = ko.observable('');
  attributeMetas = ko.observableArray<AttributeMeta>();
  archived = ko.observable<boolean>(false);
  allowUseAsTrialFactor = ko.observable<boolean>(true);
  allowUseAsTraitAdditionalDimension = ko.observable<boolean>(true);
  allowUseAsTraitRecordSelection = ko.observable<boolean>(true);
  supportsStages = ko.observable<boolean>(false);

  orderedAttributes = new OrderedEntities(this.attributeMetas);

  editUrl = ko.pureComputed(() => {
    return '/dimensions/' + this.id() + '/';
  });

  recordsUrl = ko.pureComputed(() => {
    if (this.slug() === CV_STAGE_SLUG) {
      return '/stages/';
    } else if (this.slug() === TRIAL_TYPE_SLUG) {
      return '/trial_types/';
    }

    return '/dimensions/' + this.id() + '/elements/';
  });

  canViewAndEditRecords = ko.pureComputed(() => {
    return !this.isDate() && canViewDimensionRecord();
  });

  canRemove = ko.pureComputed(() => {
    return !this.slug();
  });

  constructor(data?: DimensionMetaData) {
    this.initFromData(data);
  }

  initFromData(data?: DimensionMetaData) {
    if (data) {
      this.id(data.id);
      this.isDate(data.is_date);
      this.slug(data.slug);
      this.nameJson(data.name_json);
      this.nameSlug(data.name_slug);
      this.nameTemplate(data.name_template);
      this.archived(data.archived);
      this.allowUseAsTrialFactor(data.allow_use_as_trial_factor);
      this.allowUseAsTraitAdditionalDimension(data.allow_use_as_trait_additional_dimension);
      this.allowUseAsTraitRecordSelection(data.allow_use_as_trait_record_selection);
      this.supportsStages(data.supports_stages);

      if (data.attribute_metas) {
        this.attributeMetas(data.attribute_metas.map((data) => new AttributeMeta(data)));
        this.orderedAttributes = new OrderedEntities(this.attributeMetas);
      }
    }

    this.slugGenerator = new SlugGenerator(this.nameJson, null, this.nameSlug, {
      canEdit: !this.id(),
      fillIfEmpty: true,
    });
  }

  dispose() {
    for (let attributeMeta of this.attributeMetas()) {
      attributeMeta.dispose();
    }
    this.slugGenerator.dispose();
  }

  toData(): DimensionMetaData {
    return {
      id: this.id(),
      name_json: this.nameJson(),
      slug: this.slug(),
      is_date: this.isDate(),
      name_slug: this.nameSlug(),
      name_template: this.nameTemplate(),
      attribute_metas: this.isDate()
        ? []
        : this.attributeMetas().map((attributeMeta) => attributeMeta.toData()),
      archived: this.archived(),
      allow_use_as_trial_factor: this.allowUseAsTrialFactor(),
      allow_use_as_trait_additional_dimension: this.allowUseAsTraitAdditionalDimension(),
      allow_use_as_trait_record_selection: this.allowUseAsTraitRecordSelection(),
      supports_stages: this.supportsStages(),
    };
  }

  makeDynamicAttributes() {
    return this.attributeMetas().map((attributeMeta) => makeDynamicAttribute(attributeMeta, {}));
  }

  attributeHeaders(): { id: string; header: string }[] {
    return this.makeDynamicAttributes().map((attribute) => ({
      id: attribute.getAttrName(),
      header: translate(attribute.nameJson()),
    }));
  }

  attributeColumns(): IslandGridAttributeColumn[] {
    return this.attributeMetas().map((attributeMeta) => {
      const kind = attributeMeta.listKind;
      const choiceList = attributeMeta.choiceList();

      return {
        id: 'attr_' + attributeMeta.id(),
        header: translate(attributeMeta.nameJson()),
        kind: kind,
        // The stored value is the choice's id, so that is what the picker sends.
        choices:
          kind === 'choice' && choiceList
            ? choiceList.choices.map((choice) => ({
                value: String(choice.id),
                label: translate(choice.name_json),
              }))
            : undefined,
      };
    });
  }

  getAttributeFilters(initialFilterValues?: AttributeFilters): FilterDelegate[] {
    this.attributeFilterToSlug = new Map<FilterDelegate, string>();
    const newFilters: FilterDelegate[] = [];

    this.makeDynamicAttributes().forEach((attribute) => {
      const filter = attribute.asFilterDelegate();
      if (filter) {
        this.attributeFilterToSlug.set(filter, attribute.getAttrName());
        newFilters.push(filter);

        if (initialFilterValues && initialFilterValues[attribute.getAttrName()]) {
          updateFilterValue(filter, initialFilterValues[attribute.getAttrName()]);
        }
      }
    });

    return newFilters;
  }

  /**
   * Returns an object with the filter slug as key and the
   * serialized filter value as value.
   *
   * @example { attr_1: "apple", attr_2: "oran" }
   */
  getAttributeFilterValuesBySlug() {
    return Array.from(this.attributeFilterToSlug)
      .map(([filter, slug]) => convertToParameter(filter, slug))
      .reduce((allFilters, currentFilter) => ({ ...allFilters, ...currentFilter }), {});
  }
}
